import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLocaleNavigate } from '../../../../hooks/useLocaleNavigate';

import {
  Box,
  Typography,
  Button,
  Tabs,
  IconButton,
  CartIcon,
  TabItem,
  StatusBanner,
} from '@lucid/lego';
import { useUserIdentity } from '@lucid/portal-core';
import { useStore } from '../../../../store/store';
import { useLabels } from '../../../../hooks/useLabels';
import { convertToContentfulLocale, getCurrencyCodeForLocale } from '../../../../utils/tools';
import { ORDER_BUILDER_LABELS_ID } from '../../../../config/constants';
import type { InventoryTableRow } from '../../../../components/Configurator/utils';
import AvailableVehicles from '../../../../components/AvailableVehicles/AvailableVehicles';
import { useGetCheckoutByShortcode } from '../../../../api/services/lupp/inventory/inventory';
import { useCartContext, useCreateCart, CartApi, useAddItemsToCart, CartItem } from '@lucid/cart';
import { ImageBasedConfigurator } from '@rmp-configurator/ui';
import '@rmp-configurator/ui/styles';
import styles from './Content.module.scss';

const CONFIGURATOR_BASE_URL = import.meta.env.VITE_CONFIGURATOR_BASE_URL;
const SHORTCODE_BASE_URL = import.meta.env.VITE_SHORTCODE_BASE_URL;
const MAX_CART_QUANTITY = 50;

const getSelectionKey = (vehicle: InventoryTableRow) => vehicle.vin || vehicle.batchNumber;

const mergeSelection = (
  previous: InventoryTableRow[],
  selected: InventoryTableRow[],
  visibleRows?: InventoryTableRow[]
) => {
  if (!visibleRows) {
    return selected;
  }

  const visibleKeys = new Set(visibleRows.map(getSelectionKey));
  const retainedRows = previous.filter((vehicle) => !visibleKeys.has(getSelectionKey(vehicle)));
  const mergedRows = [...retainedRows, ...selected];
  const uniqueRows = new Map(mergedRows.map((vehicle) => [getSelectionKey(vehicle), vehicle]));

  return Array.from(uniqueRows.values());
};

export type ContentProps = {
  theme: 'dark' | 'light';
  locale: string;
};

/**
 * Order Builder page content — rendered inside CartProvider to access cart context.
 * @component
 * @param {ContentProps} props
 * @returns {JSX.Element}
 * @example
 * <Content theme="light" locale="en-US" />
 */
const Content: React.FC<ContentProps> = ({ theme, locale }) => {
  const { userIdentity } = useUserIdentity();
  const storedSelectedVehicles = useStore((state) => state.selectedOrderBuilderVehicles);
  const setStoredSelectedVehicles = useStore((state) => state.setSelectedOrderBuilderVehicles);
  const clearStoredSelectedVehicles = useStore((state) => state.clearSelectedOrderBuilderVehicles);
  const customerId = userIdentity?.businessOrganizationId ?? '';
  const navigate = useLocaleNavigate();
  const rawNavigate = useNavigate();
  const location = useLocation();
  const activeTab = location.hash.replace('#', '') || 'available-vehicles';

  const { data: labels } = useLabels({
    id: ORDER_BUILDER_LABELS_ID,
    locale: convertToContentfulLocale(locale),
  });

  const [selectedVehicles, setSelectedVehicles] =
    useState<InventoryTableRow[]>(storedSelectedVehicles);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isRemovingFromCart, setIsRemovingFromCart] = useState(false);
  const [selectionResetKey, setSelectionResetKey] = useState(0);
  const [configuratorResetKey, setConfiguratorResetKey] = useState(0);
  const [cartError, setCartError] = useState<string | null>(null);
  const [cartSuccess, setCartSuccess] = useState<string | null>(null);
  const [cartWarning, setCartWarning] = useState<string | null>(null);

  useEffect(() => {
    setSelectedVehicles(storedSelectedVehicles);
  }, [storedSelectedVehicles]);

  useEffect(() => {
    if (!cartWarning) return;
    const timeoutId = setTimeout(() => setCartWarning(null), 5000);
    return () => clearTimeout(timeoutId);
  }, [cartWarning]);

  useEffect(() => {
    if (!cartError) return;
    const timeoutId = setTimeout(() => setCartError(null), 3000);
    return () => clearTimeout(timeoutId);
  }, [cartError]);

  useEffect(() => {
    if (!cartSuccess) return;
    const timeoutId = setTimeout(() => setCartSuccess(null), 3000);
    return () => clearTimeout(timeoutId);
  }, [cartSuccess]);

  const { activeCart, refetchCart } = useCartContext();
  const { mutateAsync: createCart } = useCreateCart({ customerId });
  const { mutateAsync: addCartItems } = useAddItemsToCart();
  const cartItemCount =
    activeCart?.items?.reduce((sum, item) => sum + (item.quantityOrdered ?? 1), 0) ?? 0;
  const { mutateAsync: checkVehicleByShortcode } = useGetCheckoutByShortcode();

  const availableVehiclesBannerRef = useRef<HTMLDivElement>(null);
  const configuratorBannerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!(cartSuccess || cartWarning || cartError)) return;
    const ref = activeTab === 'configurator' ? configuratorBannerRef : availableVehiclesBannerRef;
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [cartSuccess, cartWarning, cartError, activeTab]);

  const cartItemVins = useMemo(
    () => activeCart?.items?.map((item) => item.itemCode) ?? [],
    [activeCart?.items]
  );

  const cartVinSet = useMemo(() => new Set(cartItemVins), [cartItemVins]);

  const selectedCartVehicles = useMemo(
    () => selectedVehicles.filter((v) => cartVinSet.has(v.vin)),
    [selectedVehicles, cartVinSet]
  );

  const selectedNonCartVehicles = useMemo(
    () => selectedVehicles.filter((v) => !cartVinSet.has(v.vin)),
    [selectedVehicles, cartVinSet]
  );

  const handleVehicleSelect = useCallback((_vehicle: InventoryTableRow) => {
    // Single-row click — no action needed; selection handles multi-select
  }, []);

  const handleSelectionChange = useCallback(
    (selected: InventoryTableRow[], visibleRows?: InventoryTableRow[]) => {
      setSelectedVehicles((previous) => {
        const nextSelection = mergeSelection(previous, selected, visibleRows);
        setStoredSelectedVehicles(nextSelection);
        return nextSelection;
      });
    },
    [setStoredSelectedVehicles]
  );

  const handleAddToCart = useCallback(async () => {
    if (selectedNonCartVehicles.length === 0) return;
    if (cartItemCount + selectedNonCartVehicles.length > MAX_CART_QUANTITY) {
      const remaining = MAX_CART_QUANTITY - cartItemCount;
      setCartWarning(
        remaining > 0
          ? `You can only add ${remaining} more vehicle(s). The cart limit is ${MAX_CART_QUANTITY}.`
          : `Cart limit of ${MAX_CART_QUANTITY} vehicles reached.`
      );
      return;
    }
    setIsAddingToCart(true);
    try {
      const cartItems: CartItem[] = selectedNonCartVehicles.map((vehicle) => ({
        itemName: vehicle.model ?? vehicle.vin,
        itemCode: vehicle.vin || vehicle.batchNumber,
        itemDescription: vehicle.batchNumber,
        itemCategory: 'VEHICLE',
        unitOfMeasurement: 'EA',
        quantityOrdered: 1,
        unitPrice: vehicle.price,
        itemContext: { ...vehicle, orderType: 'INVENTORY' },
      }));

      let cartId = activeCart?.cartId;

      if (!cartId) {
        const newCart = await createCart({
          customerContext: { customerId },
          currencyCode: getCurrencyCodeForLocale(locale),
        });
        cartId = newCart.cart.cartId;
      }

      await addCartItems({
        cartId: cartId!,
        customerId,
        items: cartItems,
      });

      setCartError(null);
      setCartSuccess('Item(s) added to cart successfully.');
      setSelectedVehicles([]);
      clearStoredSelectedVehicles();
      setSelectionResetKey((k) => k + 1);
      void refetchCart();
    } catch {
      setCartError(
        labels?.['cart.addError'] ??
          'We were unable to add the vehicle(s) to your cart. Please try again.'
      );
    } finally {
      setIsAddingToCart(false);
    }
  }, [
    selectedNonCartVehicles,
    activeCart,
    createCart,
    addCartItems,
    customerId,
    locale,
    refetchCart,
    cartItemCount,
    clearStoredSelectedVehicles,
    labels,
  ]);

  const handleAddToCartFromConfigurator = useCallback(
    async (shortCode: string, quantityOrdered = 1) => {
      if (cartItemCount + quantityOrdered > MAX_CART_QUANTITY) {
        const remaining = MAX_CART_QUANTITY - cartItemCount;
        setCartWarning(
          remaining > 0
            ? `You can only add ${remaining} more vehicle(s). The cart limit is ${MAX_CART_QUANTITY}.`
            : `Cart limit of ${MAX_CART_QUANTITY} vehicles reached.`
        );
        return;
      }
      setIsAddingToCart(true);
      try {
        const vehicle = await checkVehicleByShortcode({
          shortCode: shortCode,
          orderChannel: 'BUILD_ORDER',
        });

        const cartItems: CartItem[] = [
          {
            itemName: vehicle.model ?? vehicle.vin,
            itemCode: vehicle.shortcode,
            itemDescription: vehicle.vinBatchNumber ?? vehicle.shortcode,
            itemCategory: 'VEHICLE',
            unitOfMeasurement: 'EA',
            quantityOrdered,
            unitPrice: vehicle.pricing.total,
            itemContext: {
              ...vehicle,
              vin: vehicle.vin ?? vehicle.shortcode,
              price: vehicle.pricing.total,
              orderType: 'BUILD',
            },
          },
        ];

        let cartId = activeCart?.cartId;
        if (!cartId) {
          const newCart = await createCart({
            customerContext: { customerId },
            currencyCode: getCurrencyCodeForLocale(locale),
          });
          cartId = newCart.cart.cartId;
        }

        await addCartItems({
          cartId: cartId!,
          customerId,
          items: cartItems,
        });

        setCartError(null);
        setCartSuccess('Item added to cart successfully.');
        setSelectedVehicles([]);
        clearStoredSelectedVehicles();
        setSelectionResetKey((k) => k + 1);
        setConfiguratorResetKey((k) => k + 1);
        void refetchCart();
      } catch {
        setCartError(
          labels?.['cart.addError'] ??
            'We were unable to add the vehicle(s) to your cart. Please try again.'
        );
      } finally {
        setIsAddingToCart(false);
      }
    },
    [
      addCartItems,
      checkVehicleByShortcode,
      setIsAddingToCart,
      createCart,
      activeCart?.cartId,
      locale,
      customerId,
      refetchCart,
      cartItemCount,
      clearStoredSelectedVehicles,
      labels,
    ]
  );

  const handleRemoveFromCart = useCallback(async () => {
    if (selectedCartVehicles.length === 0) return;
    const cartId = activeCart?.cartId;
    if (!cartId) return;
    setIsRemovingFromCart(true);
    try {
      await Promise.all(
        selectedCartVehicles.map((vehicle) => {
          const item = activeCart?.items?.find((i) => i.itemCode === vehicle.vin);
          if (!item?.itemId) return Promise.resolve();
          return CartApi.delete(`/v1/carts/${cartId}/items/${item.itemId}`, {
            headers: { 'X-Customer-Id': customerId },
          });
        })
      );
      setCartError(null);
      setSelectedVehicles([]);
      clearStoredSelectedVehicles();
      setSelectionResetKey((k) => k + 1);
      void refetchCart();
    } catch {
      setCartError(
        labels?.['cart.removeError'] ??
          'We were unable to remove the vehicle(s) from your cart. Please try again.'
      );
    } finally {
      setIsRemovingFromCart(false);
    }
  }, [
    selectedCartVehicles,
    activeCart,
    customerId,
    refetchCart,
    clearStoredSelectedVehicles,
    labels,
  ]);

  const cartToolbarButton = useMemo(
    () => (
      <div className={styles.toolbarActions}>
        {cartItemVins.length > 0 && (
          <Button
            theme={theme}
            size="small"
            type="primary"
            color="error-2"
            dataTestId="remove-from-cart-button"
            gaComponent="order-builder"
            onClick={handleRemoveFromCart}
            disabled={selectedCartVehicles.length === 0 || isRemovingFromCart}
            iconLeft={{ category: 'global', name: 'trash' }}
            label={
              isRemovingFromCart
                ? (labels?.removing ?? 'Removing...')
                : (labels?.removeFromCart ?? 'Remove from Cart')
            }
          />
        )}
        <Button
          theme={theme}
          size="small"
          type="primary"
          dataTestId="add-to-cart-button"
          gaComponent="order-builder"
          onClick={handleAddToCart}
          disabled={selectedNonCartVehicles.length === 0 || isAddingToCart}
          iconLeft={{ category: 'global', name: 'cart' }}
          label={
            isAddingToCart ? (labels?.adding ?? 'Adding...') : (labels?.addToCart ?? 'Add to Cart')
          }
        />
      </div>
    ),
    [
      theme,
      handleAddToCart,
      handleRemoveFromCart,
      selectedNonCartVehicles.length,
      selectedCartVehicles.length,
      cartItemVins.length,
      isAddingToCart,
      isRemovingFromCart,
      labels,
    ]
  );

  const tabs: TabItem[] = useMemo(
    () => [
      {
        id: 'available-vehicles',
        label: labels?.availableVehicles ?? 'Available Vehicles',
        content: (
          <>
            <div ref={availableVehiclesBannerRef}>
              {cartSuccess && (
                <StatusBanner
                  type="success"
                  message={cartSuccess}
                  dataTestId="order-builder-cart-success"
                  className={styles.statusBanner}
                />
              )}
              {cartWarning && (
                <StatusBanner
                  type="warning"
                  message={cartWarning}
                  dataTestId="order-builder-cart-warning"
                  className={styles.statusBanner}
                />
              )}
              {cartError && (
                <StatusBanner
                  type="error"
                  message={cartError}
                  dataTestId="order-builder-cart-error"
                  className={styles.statusBanner}
                />
              )}
            </div>
            <AvailableVehicles
              onVehicleSelect={handleVehicleSelect}
              onSelectionChange={handleSelectionChange}
              toolbarContent={cartToolbarButton}
              cartVins={cartItemVins}
              selectionResetKey={selectionResetKey}
              dataTestId="order-builder-inventory"
            />
          </>
        ),
      },
      {
        id: 'configurator',
        label: labels?.configurator ?? 'Configurator',
        content: (
          <>
            <div ref={configuratorBannerRef}>
              {cartSuccess && (
                <StatusBanner
                  type="success"
                  message={cartSuccess}
                  dataTestId="order-builder-configurator-cart-success"
                  className={styles.statusBanner}
                />
              )}
              {cartWarning && (
                <StatusBanner
                  type="warning"
                  message={cartWarning}
                  dataTestId="order-builder-configurator-cart-warning"
                  className={styles.statusBanner}
                />
              )}
              {cartError && (
                <StatusBanner
                  type="error"
                  message={cartError}
                  dataTestId="order-builder-configurator-cart-error"
                  className={styles.statusBanner}
                />
              )}
            </div>
            <Box className="flex-1 flex flex-col w-full min-h-[960px]">
              <ImageBasedConfigurator
                key={configuratorResetKey}
                onlyShowcart={true}
                configureTab={{
                  showCart: false,
                  defaultRegion: locale,
                  defaultChannel: 'IMPORTER',
                }}
                showTabs={{
                  configure: true,
                }}
                apiUrl={CONFIGURATOR_BASE_URL}
                hideFilterBar={true}
                addToGlobalCart={handleAddToCartFromConfigurator}
                shortCodeUrl={SHORTCODE_BASE_URL}
              />
            </Box>
          </>
        ),
      },
    ],
    [
      labels,
      handleVehicleSelect,
      handleSelectionChange,
      handleAddToCartFromConfigurator,
      cartToolbarButton,
      cartItemVins,
      selectionResetKey,
      configuratorResetKey,
      cartError,
      cartSuccess,
      cartWarning,
      locale,
    ]
  );

  return (
    <Box
      theme={theme}
      data-testid="order-builder-page"
      id="order-builder-container"
      className="flex flex-col flex-1 h-full w-full min-h-0 overflow-hidden"
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
        #order-builder-container .stretching-tabs-container { display: flex !important; flex-direction: column !important; flex: 1 1 0% !important; min-height: 0 !important; width: 100% !important; }
        #order-builder-container .stretching-tabs-container > div:last-child { display: flex !important; flex-direction: column !important; flex: 1 1 0% !important; min-height: 0 !important; width: 100% !important; overflow-y: auto !important; overflow-x: hidden !important; }
        #order-builder-container .stretching-tabs-container div[role="tabpanel"] { display: flex !important; flex-direction: column !important; flex: 1 1 0% !important; min-height: 0 !important; width: 100% !important; }
        #order-builder-container .stretching-tabs-container div[role="tabpanel"][hidden] { display: none !important; }
      `,
        }}
      />
      <Box className={styles.header}>
        <Typography variant="headline" size="small" color="primary-1" theme={theme}>
          {labels?.orderBuilder ?? 'Order Builder'}
        </Typography>
        <IconButton
          theme={theme}
          size="large"
          color="brand"
          buttonType="primary"
          dataTestId="cart-button"
          gaComponent="order-builder"
          handleClick={() => navigate('/order-hub/order-builder/checkout')}
          customIcon={
            <CartIcon size={32} color="white" count={cartItemCount} badgeColor="#000000" />
          }
          shape="circle"
        />
      </Box>
      <Box pt={24} className="flex-1 flex flex-col min-h-0 w-full">
        <Tabs
          tabs={tabs}
          activeTab={activeTab}
          defaultActiveTab="available-vehicles"
          onTabChange={(tabId) => rawNavigate({ hash: tabId }, { replace: true })}
          theme={theme}
          dataTestId="order-builder-tabs"
          className="stretching-tabs-container"
        />
      </Box>
    </Box>
  );
};

export default Content;
